import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import {
  MANDATORY_CATEGORY,
  MANDATORY_CATEGORY_ALWAYS_EXPANDED,
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
} from 'legacy/lut/components/widgets/public/partner360/planning/config'
import { getCategoryNAField } from 'legacy/lut/components/widgets/public/partner360/planning/helpers/getCategoryNAField'

import { IJBPPlanning, SMBFractionTypes } from 'partner360/entities/planning/model'
import { isSectionSettingOn } from 'partner360/helpers/sectionSettings/isSectionSettingOn'

/** Section settings as the planning widget keeps them: category flag key -> value */
export type TSmbSectionSettings = Record<string, unknown>

interface ISmbCategoryApplicability {
  hideIfEmpty?: boolean
  isDataPresent?: boolean
}

/**
 * Whether a category is marked Not applicable, using the same rule the planning widget
 * applies: a mandatory category can never be switched off, any other one is Not applicable
 * once its own section-settings flag is set.
 */
export const isSmbCategoryNotApplicable = (
  category: string,
  mandatoryType: number,
  sectionSettings: TSmbSectionSettings,
  applicability: ISmbCategoryApplicability = {}
): boolean => {
  const isAllowed =
    mandatoryType !== MANDATORY_CATEGORY && mandatoryType !== MANDATORY_CATEGORY_ALWAYS_EXPANDED
  if (!isAllowed) {
    return false
  }

  const explicitSetting = sectionSettings[getCategoryNAField(category)]
  if (explicitSetting !== undefined && explicitSetting !== null) {
    return isSectionSettingOn(explicitSetting)
  }

  return !!applicability.hideIfEmpty && !applicability.isDataPresent
}

const DATA_PRESENT_FIELDS = [
  'SellThruSumLCNdp',
  'SellThruSumLCNet',
  'SellThruSumUSDNdp',
  'SellThruSumUSDNet',
  'SellThruUnits',
  'SellThruUnitsNdp',
  'ContractualSumUSDNet',
  'ContractualSumLCNet',
  'ContractualUnits',
  'ContractualSumUSDNdp',
  'ContractualSumLCNdp',
  'ContractualUnitsNdp',
] as const satisfies readonly (keyof IJBPPlanning)[]

const getFormatKey = (category: string, subCategory: string, l3Category: string) =>
  `${category}\u0000${subCategory}\u0000${l3Category}`

export interface ICreateSmbCategoryNotApplicableResolver {
  formats: IFormActualsTargetsSettings[]
  planningData: IJBPPlanning[]
  partnerId: string
  programType: string
  fYear: number
  globalCategory?: string | null
  sectionSettings: TSmbSectionSettings
}

/**
 * Reconstructs every category's Planning N/A state for the Offering-wide modal. Besides an
 * explicit section flag, Planning treats a HideIfEmpty category with no Total target amount
 * as N/A; all inputs needed for the same decision are already available to this feature.
 */
export const createPlanningSmbCategoryNotApplicableResolver = ({
  formats,
  planningData,
  partnerId,
  programType,
  fYear,
  globalCategory,
  sectionSettings,
}: ICreateSmbCategoryNotApplicableResolver) => {
  const relevantFormats = formats.filter((format) => format.ProgramType === programType)
  const acceptedFormatKeys = new Set(
    relevantFormats.map((format) =>
      getFormatKey(format.Category, format.SubCategory, format.L3Category)
    )
  )
  const hideIfEmptyCategories = new Set(
    relevantFormats.filter((format) => !!format.HideIfEmpty).map((format) => format.Category)
  )
  const categoriesWithData = new Set<string>()
  const fromFYear = Math.max(fYear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR)

  planningData.forEach((row) => {
    const isInScope =
      row.PartnerId === partnerId &&
      row.ProgramTypes === programType &&
      row.FYear >= fromFYear &&
      row.FYear <= fYear + 2 &&
      row.FQuarter >= 1 &&
      row.FQuarter <= 5 &&
      !!row.IsTarget &&
      !row.IsDirect &&
      !row.IsRollUp &&
      !row.IsTotal &&
      !row.IsPartnerCount &&
      (!globalCategory || row.GlobalCategory === globalCategory) &&
      (row.SMBFraction || SMBFractionTypes.Total) === SMBFractionTypes.Total &&
      acceptedFormatKeys.has(getFormatKey(row.Category, row.SubCategory, row.L3Category))

    if (isInScope && DATA_PRESENT_FIELDS.some((field) => !!row[field])) {
      categoriesWithData.add(row.Category)
    }
  })

  return (category: string, mandatoryType: number): boolean =>
    isSmbCategoryNotApplicable(category, mandatoryType, sectionSettings, {
      hideIfEmpty: hideIfEmptyCategories.has(category),
      isDataPresent: categoriesWithData.has(category),
    })
}
